import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

// =============================================================================
// Shared neighbour-data status widgets.
//
//   * NeighborDataChip — compact status pill that every analyser
//     panel drops into the AnalyzerHeader right-content slot. Clicks
//     open a details popover with the stale / missing serials.
//
//   * NeighborDataBlock — full-width card shown inline when a
//     running analyser returns 409 (completeness gate) or when the
//     operator opens the help popover and wants to fire a scan.
// =============================================================================

const SECONDS_IN_DAY = 24 * 60 * 60;
const SECONDS_IN_HOUR = 60 * 60;
const SECONDS_IN_MINUTE = 60;

export const formatAgeSeconds = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  const s = Math.max(0, Math.round(seconds));
  if (s >= SECONDS_IN_DAY) {
    const days = Math.floor(s / SECONDS_IN_DAY);
    return days === 1 ? '1 day' : `${days} days`;
  }
  if (s >= SECONDS_IN_HOUR) {
    const hours = Math.floor(s / SECONDS_IN_HOUR);
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  if (s >= SECONDS_IN_MINUTE) {
    const minutes = Math.floor(s / SECONDS_IN_MINUTE);
    return minutes === 1 ? '1 min' : `${minutes} min`;
  }
  return `${s}s`;
};

// classifyFreshness returns a {severity, label, tooltip} triple that
// every chip / card consumer uses so colour + wording stay consistent.
export const classifyFreshness = (freshness) => {
  if (!freshness) {
    return { severity: 'idle', label: 'Neighbour data: checking…', tooltip: 'Loading neighbour data status.' };
  }
  const missing = freshness.devices_missing_data || 0;
  const stale = freshness.stale_count || 0;
  const totalConnected = freshness.connected_jnet2_devices || 0;
  const caps = freshness.cap_devices || 0;
  const capNote = caps > 0
    ? ` CAPs (${caps}) are excluded — they have AP-only radios and cannot scan.`
    : '';
  if (missing > 0) {
    return {
      severity: 'error',
      label: missing === 1 ? '1 Node missing scan' : `${missing} Nodes missing scan`,
      tooltip: `Analysers use golden neighbour scans. They cannot run until every connected Node has a golden snapshot. ${missing} of ${totalConnected} Node(s) have no golden report on file.${capNote}`,
    };
  }
  if (stale > 0) {
    return {
      severity: 'warning',
      label: stale === 1 ? '1 Node with stale scan' : `${stale} Nodes with stale scan`,
      tooltip: `Analysers can still run, but ${stale} Node(s) have a golden neighbour report older than ${formatAgeSeconds(freshness.stale_threshold_s)}. Oldest: ${freshness.oldest_serial || '—'} (${formatAgeSeconds(freshness.oldest_age_s)}).${capNote}`,
    };
  }
  if (totalConnected === 0) {
    return {
      severity: 'idle',
      label: caps > 0 ? `No connected Nodes (${caps} CAP${caps === 1 ? '' : 's'})` : 'No connected Nodes',
      tooltip: caps > 0
        ? `No scan-capable Nodes are connected. ${caps} CAP(s) are present but cannot scan; analysers have nothing to plan against.`
        : 'No connected Nodes in this project — neighbour scans are not applicable.',
    };
  }
  return {
    severity: 'success',
    label: `All Nodes reported (golden) · oldest ${formatAgeSeconds(freshness.oldest_age_s)}`,
    tooltip: `${totalConnected} connected Node(s) have a golden neighbour snapshot. Oldest: ${freshness.oldest_serial || '—'} (${formatAgeSeconds(freshness.oldest_age_s)}).${capNote}`,
  };
};

const iconForSeverity = (sev) => {
  if (sev === 'error') return <ErrorOutlineIcon fontSize="small" />;
  if (sev === 'warning') return <WarningAmberIcon fontSize="small" />;
  if (sev === 'success') return <CheckCircleOutlineIcon fontSize="small" />;
  return null;
};

const colorForSeverity = (sev) => {
  if (sev === 'error') return 'error';
  if (sev === 'warning') return 'warning';
  if (sev === 'success') return 'success';
  return 'default';
};

// NeighborDataChip is the compact summary pill rendered at the top
// of every analyser panel via AnalyzerHeader's rightContent slot.
// Clicking the chip opens a popover with per-device breakdown and
// the scan trigger buttons.
export const NeighborDataChip = ({
  freshness,
  loading,
  onRefresh,
  onScanMissing,
  onScanAll,
  scanningSerials = [],
  missingSerials = [],
  staleSerials = [],
  oldestSerial,
  staleThresholdS,
  oldestAgeS,
  scopeLabel = null,
}) => {
  const { severity, label, tooltip } = classifyFreshness(freshness);
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);

  const handleOpen = (e) => setAnchor(e.currentTarget);
  const handleClose = () => setAnchor(null);

  return (
    <>
      <Tooltip title={tooltip} placement="left" arrow>
        <Chip
          icon={loading ? <CircularProgress size={12} /> : iconForSeverity(severity)}
          label={label}
          size="small"
          variant="outlined"
          color={colorForSeverity(severity)}
          onClick={handleOpen}
          sx={{ cursor: 'pointer' }}
        />
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, maxWidth: 360 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
              Neighbour data status
            </Typography>
            <Tooltip title="Refresh status">
              <IconButton size="small" onClick={onRefresh} disabled={loading}>
                {loading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Staleness threshold: {formatAgeSeconds(staleThresholdS)}. Neighbour scans are read-only
            but take a few minutes per Node to complete. CAPs are excluded — they have AP-only
            radios and are observed via their children's reports.
            {scopeLabel ? ` Scope: ${scopeLabel}.` : ''}
          </Typography>

          {missingSerials.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main' }}>
                Missing neighbour report ({missingSerials.length} Node{missingSerials.length === 1 ? '' : 's'})
              </Typography>
              <List dense disablePadding sx={{ maxHeight: 140, overflow: 'auto' }}>
                {missingSerials.slice(0, 20).map((serial) => (
                  <ListItem key={serial} disableGutters disablePadding>
                    <ListItemText
                      primaryTypographyProps={{ variant: 'caption', sx: { fontFamily: 'monospace' } }}
                      primary={serial}
                    />
                  </ListItem>
                ))}
              </List>
              {missingSerials.length > 20 && (
                <Typography variant="caption" color="text.secondary">
                  +{missingSerials.length - 20} more
                </Typography>
              )}
            </Box>
          )}

          {staleSerials.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main' }}>
                Stale (&gt; {formatAgeSeconds(staleThresholdS)})
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Oldest: <Box component="span" sx={{ fontFamily: 'monospace' }}>{oldestSerial || '—'}</Box>
                {oldestAgeS != null ? ` · ${formatAgeSeconds(oldestAgeS)}` : ''}
              </Typography>
            </Box>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            {missingSerials.length > 0 && onScanMissing && (
              <Button
                size="small"
                variant="contained"
                color="error"
                disabled={scanningSerials.length > 0}
                onClick={() => { onScanMissing(); handleClose(); }}
              >
                Scan missing ({missingSerials.length})
              </Button>
            )}
            {onScanAll && (
              <Button
                size="small"
                variant={missingSerials.length > 0 ? 'outlined' : 'contained'}
                onClick={() => { onScanAll(); handleClose(); }}
              >
                Scan all connected
              </Button>
            )}
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

// NeighborDataBlock is a full-width inline card shown when an
// analyser refuses to run because neighbour data is incomplete.
// Renders the list of serials with no report and a button that
// fires POST /tree-mgmt/{serial}/neighbors for each.
export const NeighborDataIncompleteCard = ({
  missingSerials,
  onScanMissing,
  scanningSerials = [],
  onContinueWithoutNeighbors,
  continueHelp,
}) => {
  const scanning = scanningSerials.length > 0;
  return (
    <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 2 }}>
      <AlertTitle>Neighbour data is incomplete</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        The analyser cannot run until every connected Node has a golden neighbour snapshot
        (the same data the planner uses). {missingSerials.length} Node{missingSerials.length === 1 ? '' : 's'}
        {' '}{missingSerials.length === 1 ? 'has' : 'have'} no golden report on file (CAPs are
        excluded — they have AP-only radios and cannot scan):
      </Typography>
      <Box
        sx={{
          maxHeight: 120,
          overflow: 'auto',
          p: 1,
          mb: 1.5,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
          {missingSerials.map((serial) => (
            <Chip
              key={serial}
              size="small"
              variant="outlined"
              label={serial}
              sx={{ fontFamily: 'monospace' }}
            />
          ))}
        </Stack>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Run a neighbour scan, then mark reports as golden (Neighbors → All Reports) so they match
        what the planner loads. Scans can take several minutes per device.
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          disabled={scanning}
          startIcon={scanning ? <CircularProgress size={14} color="inherit" /> : null}
          onClick={onScanMissing}
        >
          {scanning ? `Scanning ${scanningSerials.length}…` : `Scan ${missingSerials.length} device${missingSerials.length === 1 ? '' : 's'}`}
        </Button>
        {onContinueWithoutNeighbors && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={scanning}
            onClick={onContinueWithoutNeighbors}
          >
            Continue without neighbour data
          </Button>
        )}
      </Stack>
      {onContinueWithoutNeighbors && continueHelp && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {continueHelp}
        </Typography>
      )}
    </Alert>
  );
};

export default NeighborDataChip;
